import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';

import {
	createNaturalPerson,
	createPersonLegal,
} from '@dealstack/frontend/app/services/Person.service.ts';
import { PersonType } from '@dealstack/shared/lib/generated';

export enum CREATE_PERSON_NATURAL_FORM_VALUES {
	FIRST_NAME = 'firstName',
	LAST_NAME = 'lastName',
}

interface CreatePersonNaturalForm {
	[CREATE_PERSON_NATURAL_FORM_VALUES.FIRST_NAME]: string;
	[CREATE_PERSON_NATURAL_FORM_VALUES.LAST_NAME]: string;
}

export enum CREATE_PERSON_LEGAL_FORM_VALUES {
	NAME = 'name',
}

interface CreatePersonLegalForm {
	[CREATE_PERSON_LEGAL_FORM_VALUES.NAME]: string;
}

export const useCreateNewEntityController = (
	dealId: string,
	onCreated: (createdEntityId: string) => void
) => {
	const commonYup = useCommonYupValidation();
	const [type, setType] = useState<PersonType>(PersonType.LEGAL);

	const createNaturalPersonRequest = useMutation({
		mutationKey: [API_PATHS.PERSON_DEAL_DEALID_PERSON_CREATE_NATURAL_POST, { dealId }],
		mutationFn: createNaturalPerson,
	});

	const createPersonLegalRequest = useMutation({
		mutationKey: [API_PATHS.PERSON_DEAL_DEALID_PERSON_CREATE_LEGAL_POST, { dealId }],
		mutationFn: createPersonLegal,
	});

	const createPersonNaturalForm = useForm<CreatePersonNaturalForm>({
		mode: 'onSubmit',
		resolver: yupResolver(
			Yup.object().shape({
				[CREATE_PERSON_NATURAL_FORM_VALUES.FIRST_NAME]: commonYup.string.required,
				[CREATE_PERSON_NATURAL_FORM_VALUES.LAST_NAME]: commonYup.string.required,
			})
		),
		defaultValues: {
			[CREATE_PERSON_NATURAL_FORM_VALUES.FIRST_NAME]: '',
			[CREATE_PERSON_NATURAL_FORM_VALUES.LAST_NAME]: '',
		},
	});

	const createPersonLegalForm = useForm<CreatePersonLegalForm>({
		mode: 'onSubmit',
		resolver: yupResolver(
			Yup.object().shape({
				[CREATE_PERSON_LEGAL_FORM_VALUES.NAME]: commonYup.string.required,
			})
		),
		defaultValues: {
			[CREATE_PERSON_LEGAL_FORM_VALUES.NAME]: '',
		},
	});

	const onSubmitNatural = async (formValues: CreatePersonNaturalForm) => {
		const { firstName, lastName } = formValues;
		createNaturalPersonRequest.mutate(
			{ dealId, createArgs: { firstName, lastName } },
			{
				onSuccess: (result) => {
					onCreated(result!.id);
				},
			}
		);
	};

	const onSubmitLegal = async (formValues: CreatePersonLegalForm) => {
		const { name } = formValues;
		createPersonLegalRequest.mutate(
			{ dealId, createArgs: { name } },
			{
				onSuccess: (result) => {
					onCreated(result!.id);
				},
			}
		);
	};

	return {
		type,
		setType,
		createPersonNaturalForm,
		createPersonLegalForm,
		onSubmitNatural,
		onSubmitLegal,
		isLegalType: type === PersonType.LEGAL,
		isLoading: createPersonLegalRequest.isPending || createNaturalPersonRequest.isPending,
	};
};
